import apiClient from '@/services/api'
import Cookies from 'js-cookie'
import moment from 'moment-timezone'
import { jwtDecode } from 'jwt-decode'
import * as Sentry from '@sentry/vue'
import { createSessionId } from './state'
import { checkInternetConnection as verifyInternetConnection } from '@/services/internet-connection'

const AUTH_EVENT_KEY = 'auth:event'

let internetConnectionCheckPromise = null

const addCleanupBreadcrumb = (reason, data = {}) => {
  if (typeof Sentry.addBreadcrumb !== 'function') {
    return
  }

  Sentry.addBreadcrumb({
    category: 'auth.cleanup',
    level: 'info',
    message: reason,
    data
  })
}

export default {
  setLoading({ commit }, payload) {
    commit('SET_LOADING', payload)
  },
  async checkInternetConnection({ commit }) {
    if (internetConnectionCheckPromise) {
      return internetConnectionCheckPromise
    }

    commit('setInternetConnectionState', {
      checking: true
    })

    internetConnectionCheckPromise = verifyInternetConnection()
      .then(stable => {
        commit('setInternetConnectionState', {
          checking: false,
          stable,
          lastCheckedAt: Date.now()
        })

        return stable
      })
      .catch(() => {
        commit('setInternetConnectionState', {
          checking: false,
          stable: false,
          lastCheckedAt: Date.now()
        })

        return false
      })
      .finally(() => {
        internetConnectionCheckPromise = null
      })

    return internetConnectionCheckPromise
  },
  async logoutAndReset(
    { commit, state },
    {
      reason = 'unknown',
      shouldCallSignOutApi = false,
      refreshToken = null,
      broadcast = true,
      redirectToAuth = true
    } = {}
  ) {
    if (state.isLoggingOut) {
      if (redirectToAuth && typeof window !== 'undefined' && window.location.hash !== '#/auth') {
        window.location.hash = '#/auth'
      }
      return
    }

    commit('SET_LOGGING_OUT', true)

    const previousSessionId = state.sessionId
    const refreshTokenForSignOut = refreshToken ?? Cookies.get('refresh')

    try {
      if (shouldCallSignOutApi && refreshTokenForSignOut) {
        await apiClient.post(
          '/api/user/sign_out',
          {
            refresh_token: refreshTokenForSignOut
          },
          {
            not_required: true,
            skipAuthRefresh: true
          }
        )
      }
    } catch (_) {
    } finally {
      commit('clearApplicationState', {
        preserve: {
          isLoggingOut: true
        }
      })

      if (broadcast && typeof localStorage !== 'undefined') {
        localStorage.setItem(
          AUTH_EVENT_KEY,
          JSON.stringify({
            type: 'logout',
            reason,
            previousSessionId,
            nextSessionId: state.sessionId,
            eventId: createSessionId(),
            timestamp: Date.now()
          })
        )
      }

      addCleanupBreadcrumb('logout_and_reset', {
        reason,
        shouldCallSignOutApi
      })

      commit('SET_LOGGING_OUT', false)

      if (redirectToAuth && typeof window !== 'undefined' && window.location.hash !== '#/auth') {
        window.location.hash = '#/auth'
      }
    }
  },
  async loadApplicationState({ commit, state, dispatch }) {
    const expectedSessionId = state.sessionId

    if (state.token === null) {
      return
    }

    try {
      const [associateResponse, projectResponse, userResponse] = await Promise.all([
        apiClient.get('/api/init/current_associate', { not_required: true }),
        apiClient.get('/api/init/current_project', { not_required: true }),
        apiClient.get('/api/init/current_user', { not_required: true })
      ])

      const { data: associateData } = associateResponse
      const { data: projectData } = projectResponse
      const { data: userData } = userResponse

      if (state.sessionId !== expectedSessionId || state.token === null) {
        return
      }

      if (userData.user.timezone) {
        moment.tz.setDefault(userData.user.timezone)
        commit('setTimeZone', userData.user.timezone)
      } else {
        moment.tz.setDefault()
        commit('setTimeZone', moment.tz.guess())
      }

      Sentry.setUser({
        id: String(associateData.associate.id)
      })

      const [
        profilesResponse,
        legalEntitiesResponse,
        rolesResponse,
        departmentsResponse,
        authorizationsResponse,
        projectsResponse,
        featuresResponse,
        timeConstantsResponse,
        workAreasResponse,
        reportsResponse,
        payCodesResponse,
        paymentSystemsResponse,
        dsdResponse,
        responsibleContactsResponse
      ] = await Promise.all([
        apiClient.post(
          '/api/associate/profiles/get/new',
          {
            project_id: projectData.project.id,
            associate_number: associateData.associate.associate_number,
            with_disabled: false
          },
          { not_required: true }
        ),
        apiClient.post(
          '/api/project/legal_entity/get_all?fields={id,name,parent_entity}',
          {
            project_id: projectData.project.id,
            page: 1,
            size: 1000
          },
          { not_required: true }
        ),
        apiClient.post(
          '/api/project/role/get_all?fields={id,name,parent_entity,hourly_rate,orientation_rate,training_rate}',
          {
            project_id: projectData.project.id
          },
          { not_required: true }
        ),
        apiClient.get('/api/departments?fields={id,name,parent_entity}', {
          params: { project_id: projectData.project.id },
          not_required: true
        }),
        apiClient.post('/api/get_shift_auth_types', {}, { not_required: true }),
        apiClient.get('/api/init/user_projects', { not_required: true }),
        apiClient.post(
          `/api/project/${projectData.project.id}/features/get`,
          {},
          {
            not_required: true
          }
        ),
        apiClient.post(
          '/api/get_project_time_constants',
          {
            project_id: projectData.project.id
          },
          { not_required: true }
        ),
        apiClient.post(
          '/api/project/work_area/get_all',
          {
            project_id: projectData.project.id,
            page: 1,
            size: 1000
          },
          { not_required: true }
        ),
        apiClient.get('/api/epeople_reports', { not_required: true }),
        apiClient.post(
          '/api/project/pay_code/get_all',
          {
            project_id: projectData.project.id
          },
          { not_required: true }
        ),
        apiClient.post(
          '/api/get_project_payment_systems',
          {
            project_id: projectData.project.id
          },
          { not_required: true }
        ),
        apiClient.get(`/api/project/${projectData.project.id}/responsible_contacts_dcd`, { not_required: true }),
        apiClient.get(`/api/associate/${associateData.associate.id}/responsible_contacts`, { not_required: true })
      ])

      const { data: profilesData } = profilesResponse
      const { data: legalEntitiesData } = legalEntitiesResponse
      const { data: rolesData } = rolesResponse
      const { data: departmentsData } = departmentsResponse
      const { data: authorizationsData } = authorizationsResponse
      const { data: projectsData } = projectsResponse
      const { data: featuresData } = featuresResponse
      const { data: timeConstantsData } = timeConstantsResponse
      const { data: workAreasData } = workAreasResponse
      const { data: payCodesData } = payCodesResponse
      const { data: paymentSystemsData } = paymentSystemsResponse
      const { data: reportsData } = reportsResponse
      const { data: dsdData } = dsdResponse
      const { data: responsibleContactsData } = responsibleContactsResponse

      if (state.sessionId !== expectedSessionId || state.token === null) {
        return
      }

      commit('setAssociate', {
        ...associateData.associate,
        profiles: profilesData.associates
      })
      commit('setResponsibleContacts', responsibleContactsData.contacts ?? {})
      commit('setCurrentPaymentType', associateData.current_payment_type)
      commit('setCurrentPaymentTypeName', associateData.current_payment_type_name)
      commit('setCurrentProject', {
        ...projectData.project,
        features: featuresData.features,
        dsd: dsdData.contacts
      })
      commit('setUser', {
        ...userData.user,
        id: Number(userData.user.user_id)
      })
      commit('setProjectLegalEntities', legalEntitiesData.items)
      commit('setProjectRoles', rolesData.roles)
      commit('setProjectDepartments', departmentsData.departments)
      commit('setExtendedShiftAuthTypes', authorizationsData.types)
      commit('setAllProjects', projectsData.projects)
      commit('setTimeConstants', timeConstantsData.time_constants)
      commit('setWorkAreas', workAreasData.items)
      commit('setReports', reportsData.report)
      commit('setProjectPayCodes', payCodesData.pay_codes)
      commit(
        'setProjectPaymentSystems',
        paymentSystemsData.payment_systems.filter(item => {
          const projectAssociation = item.payment_system_project_association.find(
            project => project.project.id === projectData.project.id
          )

          return (
            projectAssociation &&
            projectAssociation.configs.enabled === true &&
            projectAssociation.configs.activated === true
          )
        })
      )

      commit('SET_LOADING', false)

      if (state.sessionId !== expectedSessionId || state.token === null) {
        return
      }

      localStorage.setItem('applicationState', JSON.stringify(state))

      await Promise.all([
        dispatch('bookmarks/getBookmarks', null, { root: true }),
        dispatch('pointSystem/getFullInfo', null, { root: true })
      ])
    } catch (exception) {
      console.log(exception)
    }
  },
  updateLocalStorage({ state }, { expectedSessionId } = {}) {
    if (state.token === null) {
      localStorage.removeItem('applicationState')
      return
    }

    if (expectedSessionId && expectedSessionId !== state.sessionId) {
      return
    }

    localStorage.setItem('applicationState', JSON.stringify(state))
  },
  async signUp(_, payload) {
    try {
      const { data } = await apiClient.post('/api/user_sign_up', {
        ...payload
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.profile
    } catch (error) {
      throw error.message
    }
  },
  async getAgreement() {
    try {
      const { data } = await apiClient.post('/api/get_app_agreements')

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },
  async verifySignUp(_, { email, phone }) {
    try {
      const { data } = await apiClient.post('/api/verify_sign_up_data', {
        email,
        phone
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async signIn(_, { email, password }) {
    try {
      const { data } = await apiClient.post('/api/user_sign_in', {
        email,
        password
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.profile
    } catch (error) {
      throw error.message
    }
  },
  async verifyEmail(_, { email }) {
    try {
      const { data } = await apiClient.post('/api/sign_in/verify/email', {
        email
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.profile
    } catch (error) {
      throw error.message
    }
  },
  async verifyPhone(_, { phone_number }) {
    try {
      const { data } = await apiClient.post('/api/sign_in/verify/phone_number', {
        phone_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.profile
    } catch (error) {
      throw error.message
    }
  },
  async signOut({ dispatch }) {
    await dispatch('logoutAndReset', {
      reason: 'manual_sign_out',
      shouldCallSignOutApi: true,
      broadcast: true,
      redirectToAuth: true
    })
  },
  async sendVerificationCode(_, { form }) {
    try {
      const { data } = await apiClient.post('/api/verification_code/send', {
        ...form,
        from_web: true
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },
  async getToken({ commit, dispatch }, { user_id, private_key }) {
    try {
      const { data } = await apiClient.post('/api/token', {
        user_id,
        private_key
      })

      if (data.access && data.refresh) {
        Cookies.set('access', data.access, {
          expires: new Date(jwtDecode(data.access).exp * 1000)
        })
        Cookies.set('refresh', data.refresh, {
          expires: new Date(jwtDecode(data.refresh).exp * 1000)
        })

        commit('rotateSessionId')
        commit('setToken', data.access)

        await dispatch('loadApplicationState')
      } else {
        throw new Error('Token is not defined')
      }
    } catch (error) {
      throw error.message
    }
  },
  async refreshToken(_, { refresh }) {
    try {
      const { data } = await apiClient.post('/api/token/refresh', {
        refresh
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async checkVerificationCode(_, { form }) {
    try {
      const { data } = await apiClient.post('/api/verification_code/check', {
        ...form,
        from_web: true
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },
  async resetPassword(_, { email, password }) {
    try {
      const { data } = await apiClient.post('/api/reset_password', {
        email,
        password
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },
  async switchAssociate(_, payload) {
    try {
      const { data } = await apiClient.post('/api/associate/switch', {
        ...payload
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async switchProject(_, payload) {
    try {
      const { data } = await apiClient.post('/api/switch_current_project/new', {
        ...payload
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async switchPaymentType(_, { payment_type_id }) {
    try {
      const { data } = await apiClient.post('/api/tc_set_admin_payment_type', {
        payment_type_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },
  async uploadFile(_, { files }) {
    try {
      const formData = new FormData()

      files.forEach(file => formData.append(file.name, file))

      const { data } = await apiClient.post('/api/upload_files', formData)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.ids
    } catch (error) {
      throw error.message
    }
  },
  async uploadFiles(_, { files, path }) {
    const formData = new FormData()

    files.forEach(file => formData.append(path ?? file.name, file))

    const { data } = await apiClient.post('/api/upload_files', formData)

    return data
  },
  async getVisitors(_, params) {
    const { page, per_page, project_id, filter } = params
    const { data } = await apiClient.post('/api/visitor/search', {
      page,
      per_page,
      project_id,
      filter
    })

    return data
  },
  async saveSignatureContext({ rootState }, { signature }) {
    try {
      const { data } = await apiClient.post('/api/context/signature/save', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number: rootState.applicationState.associate.associate_number,
        signed_date: new Date(),
        signature
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },
  async updateSignature(_, { id, file_id }) {
    try {
      const { data } = await apiClient.put(`/api/user/${id}/signature`, {
        file_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  }
}
